import type { SmartDiscoveryChatResponse } from '../../features/opportunities/opportunity-wizard/steps/smart-discovery/models/smart-discovery.models';

const DOC_ANCHOR = '0178ec9f-eb4f-4ea9-8ffa-16a46d256ac9';

/**
 * Realistic workspace Smart Discovery `POST …/conversations/{id}/chat` body for mock mode — matches production shape
 * (intent, bracketed citations in answer, knowledge-style evidence anchors, A3 authority).
 */
export const MOCK_SMART_DISCOVERY_CHAT_BODY: SmartDiscoveryChatResponse = {
  intent: 'summarize_status',
  answer:
    'The evidence covers the end-to-end process for proposal creation within Momentum, structured into three core phases: Qualification (guided flows, AI chat), Blueprint Drafting (block-based editor with AI-supported content and visuals), and Finalization (production of branded documents in Office with an add-in for live validation and compliance checks) [2][5][6][1]. Key implementation risks identified include: complex mapping from qualification to proposal drafting (core to solution value, high risk) [7], challenge of feedback loop quality when extracting new facts back into the system (medium risk) [3], and possible inconsistencies in AI-generated styles (low risk) [8]. The design addresses the need to handle diverse proposal types and mitigate context fragmentation seen in current manual, disconnected processes [9][10]. There is a notable gap in current practice: lack of systematic continuity from qualification through to final proposal and deal traceability, which the proposed solution aims to resolve [9].',
  evidence: [
    {
      id: `${DOC_ANCHOR}=section=brief-the-three-phases-phase-3-final-answer-office--add-in=9=paragraph=0`,
      label: 'Phase 3: Final Answer (Office + Add-in)',
      documentTitle: 'Phase 3: Final Answer (Office + Add-in)',
      sectionPath: ['Brief', 'The three phases', 'Phase 3: Final Answer (Office + Add-in)'],
      authorityLevel: 'A3',
      snippet:
        'The final document is produced in Word or PowerPoint using brand templates. Critically, the Momentum context does not stop at export - a custom Momentum Office Add-in (built on Office.js) runs as a task pane inside Office and maintains a live connection to the Momentum API. This enables four capabilities in Phase 3: (1…',
    },
    {
      id: `${DOC_ANCHOR}=section=brief-the-three-phases-phase-1-guided-flows-qualification=7=paragraph=0`,
      label: 'Phase 1: Guided Flows (Qualification)',
      documentTitle: 'Phase 1: Guided Flows (Qualification)',
      sectionPath: ['Brief', 'The three phases', 'Phase 1: Guided Flows (Qualification)'],
      authorityLevel: 'A3',
      snippet:
        'Momentum already owns this phase. A combination of deterministic structured UI (dimension scoring, RFP upload, guided workflows) and AI-powered chat (free-text discovery, clarifying questions) populates the Proposal Context. Everything produced here (findings, facts trail, RAG ratings, accepted overrides) becomes the f…',
    },
    {
      id: `${DOC_ANCHOR}=table=1=row=5`,
      label: 'Implementation Risks',
      documentTitle: 'Implementation Risks',
      sectionPath: ['Brief', 'Implementation Risks'],
      authorityLevel: 'A3',
      snippet:
        'Feedback loop quality | Medium | Extracting “new facts” or commitments from a finalised document back into the Momentum facts trail is inherently ambiguous. Fully automated extraction risks noise; skipping it breaks the audit trail promise. | Design a lightweight human-review step in the Finalise & Sync flow. AI su…',
    },
    {
      id: `${DOC_ANCHOR}=table=0=row=1`,
      label: 'The three phases',
      documentTitle: 'The three phases',
      sectionPath: ['Brief', 'The three phases'],
      authorityLevel: 'A3',
      snippet:
        'Tool: Deterministic UI + AI Chat Where: Inside Momentum Output: Qualification context | Tool: Block-based editor (CKEditor 5) Where: Inside Momentum Output: Proposal blueprint | Tool: Word / PowerPoint Where: Office + Add-in Output: Final client document',
    },
    {
      id: `${DOC_ANCHOR}=section=brief-the-three-phases-phase-2-blueprint-editor-drafting=8=paragraph=0`,
      label: 'Phase 2: Blueprint Editor (Drafting)',
      documentTitle: 'Phase 2: Blueprint Editor (Drafting)',
      sectionPath: ['Brief', 'The three phases', 'Phase 2: Blueprint Editor (Drafting)'],
      authorityLevel: 'A3',
      snippet:
        'A block-based proposal editor built inside Momentum. Each section (executive summary, scope response, risk mitigation, team, pricing) is a typed block or section with a rendering mode. A toggle allows switching between tender style (numbered requirements, acceptance statements) and narrative/story style (flowing execut…',
    },
    {
      id: `${DOC_ANCHOR}=table=0=row=0`,
      label: 'The three phases',
      documentTitle: 'The three phases',
      sectionPath: ['Brief', 'The three phases'],
      authorityLevel: 'A3',
      snippet: 'Phase 1 - Guided Flows | Phase 2 - Blueprint Editor | Phase 3 - Final Answer',
    },
    {
      id: `${DOC_ANCHOR}=table=1=row=2`,
      label: 'Implementation Risks',
      documentTitle: 'Implementation Risks',
      sectionPath: ['Brief', 'Implementation Risks'],
      authorityLevel: 'A3',
      snippet:
        'Phase 1→2 mapping logic | High | Qualification findings must be translated into pre-populated proposal section suggestions. This mapping (e.g. “Scope dimension findings → Scope response block”) is where the core value lives - and the core complexity. Without it, Phase 2 is just a blank editor. | Prototype the mapping w…',
    },
    {
      id: `${DOC_ANCHOR}=table=1=row=8`,
      label: 'Implementation Risks',
      documentTitle: 'Implementation Risks',
      sectionPath: ['Brief', 'Implementation Risks'],
      authorityLevel: 'A3',
      snippet:
        'Rendering mode quality | Low | Switching between tender style and narrative style per block requires distinct prompt strategies and output validation. Early versions may produce inconsistent tone, especially for short blocks. | Invest in prompt engineering and few-shot examples for each mode before release. Use real pr…',
    },
    {
      id: `${DOC_ANCHOR}=section=brief-outset-proposal-diversity--context-fragmentation-the-current-pain=3=paragraph=0`,
      label: 'The Current Pain',
      documentTitle: 'The Current Pain',
      sectionPath: [
        'Brief',
        'Outset: Proposal Diversity & Context Fragmentation',
        'The Current Pain',
      ],
      authorityLevel: 'A3',
      snippet:
        'Today, these documents are produced largely by hand, in Word or PowerPoint, with no systematic connection to the upstream qualification work. We invest a lot of time and effort into our proposals and face significant context loss between the initial Alpha phase where we ideate and draft and the writing phase, where the…',
    },
    {
      id: `${DOC_ANCHOR}=section=brief-outset-proposal-diversity--context-fragmentation=1=paragraph=0`,
      label: 'Outset: Proposal Diversity & Context Fragmentation',
      documentTitle: 'Outset: Proposal Diversity & Context Fragmentation',
      sectionPath: ['Brief', 'Outset: Proposal Diversity & Context Fragmentation'],
      authorityLevel: 'A3',
      snippet:
        'At Zühlke we have are covering a wide spectrum of proposal documents that differ fundamentally in length, structure, audience, and output format. Any tooling that tries to force a single format will either produce over-engineered responses for simple situations or under-specified ones for complex tenders. This diversit…',
    },
  ],
  followUpSuggestions: [],
};
